import { useState, type ChangeEvent, type FormEvent } from 'react';
import {
  useDevices, useSaveDevice, useDeleteDevice, useOpenProvisioning,
} from '../../lib/api/hooks-extension.js';
import { useExtensions } from '../../lib/api/hooks.js';
import { useHandsetCatalogue, useProvisioningRoles } from '../../lib/api/hooks-provisioning.js';
import { TableSkeleton, Empty, ErrorBanner, Badge, Segmented } from '../../components/ui/Primitives.js';
import { Switch } from '../../components/ui/Form.js';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { Icon } from '../../components/ui/Icon.js';
import { ApiClientError } from '../../lib/api/client.js';
import { relativeTime } from '../../lib/format.js';
import type { Device, HandsetModel, ProvisioningRoleInfo } from '@arcnet/contracts';

/**
 * Handset inventory.
 *
 * A device is separate from an extension because the two change independently:
 * a phone is re-assigned between people without the extension moving, and one
 * extension can ring on a desk phone and a softphone at once. The list is
 * therefore keyed on the MAC — the thing physically on someone's desk.
 */

const VENDORS = [
  { key: 'yealink', label: 'Yealink' },
  { key: 'grandstream', label: 'Grandstream' },
  { key: 'fanvil', label: 'Fanvil' },
  { key: 'polycom', label: 'Poly / Polycom' },
  { key: 'snom', label: 'Snom' },
  { key: 'cisco', label: 'Cisco' },
  { key: 'generic', label: 'Other' },
] as const;

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'danger' | 'muted'> = {
  online: 'ok', provisioned: 'ok', offline: 'warn',
  unprovisioned: 'muted', retired: 'muted',
};

interface LineDraft { id: string; extensionId: string; label: string }

export function DevicesTab({ tenantId }: { tenantId: string }) {
  const devices = useDevices(tenantId);
  const del = useDeleteDevice(tenantId);
  const openProv = useOpenProvisioning(tenantId);
  const save = useSaveDevice(tenantId);
  const roles = useProvisioningRoles();
  const roleNames = new Map((roles.data?.data ?? []).map((r) => [r.role, r.name] as const));
  const [editing, setEditing] = useState<Device | null>(null);
  const [creating, setCreating] = useState(false);

  /**
   * Shared desk, per handset: whether anybody may log in at it with *31.
   *
   * Here because it is a property of the phone. A person's PIN and where they
   * are sitting are on their extension. Turning it off ends any login on it --
   * the API does that, so nobody is left speaking as someone who cannot log out.
   */
  const deskToggle = useMutation({
    mutationFn: ({ id, on }: { id: string; on: boolean }) =>
      api.patch(`/v1/tenants/${tenantId}/hot-desk/devices/${id}`, { hot_desk_enabled: on }),
    onSuccess: () => void devices.refetch(),
  });

  const rows = devices.data?.data ?? [];
  const unprovisioned = rows.filter((d) => !d.mac_address).length;

  return (
    <>
      <div className="arc-row" style={{ marginBottom: 14 }}>
        <div>
          <h2 className="arc-h2">Handsets</h2>
          <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            Physical phones and the extensions on their line keys.
          </p>
        </div>
        <div className="arc-spacer" />
        <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
          onClick={() => { setCreating((v) => !v); setEditing(null); }}>
          <Icon name={creating ? 'close' : 'plus'} size={13} />
          {creating ? 'Cancel' : 'Add handset'}
        </button>
      </div>

      {devices.error && <ErrorBanner error={devices.error} />}
      {del.error && <ErrorBanner error={del.error} />}
      {deskToggle.error != null && <ErrorBanner error={deskToggle.error} />}

      {unprovisioned > 0 && !creating && !editing && (
        <div className="arc-banner arc-banner-info" style={{ marginBottom: 14 }}>
          <Icon name="info" size={14} />
          <span>
            {unprovisioned} handset{unprovisioned === 1 ? ' has' : 's have'} no MAC address, so
            {unprovisioned === 1 ? ' it' : ' they'} cannot fetch a configuration. Add the MAC from
            the label on the underside of the phone.
          </span>
        </div>
      )}

      {(creating || editing) && (
        <DeviceEditor
          tenantId={tenantId} existing={editing}
          onDone={() => { setCreating(false); setEditing(null); }}
        />
      )}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Handset</th><th>MAC</th><th>Model</th>
                <th>Lines</th><th>Status</th><th>Shared desk</th><th>Last seen</th><th />
              </tr>
            </thead>
            {devices.isLoading ? <TableSkeleton cols={8} /> : (
              <tbody>
                {rows.map((d) => (
                  <tr key={d.id}>
                    <td style={{ fontWeight: 550 }}>
                      {d.label ?? <span className="arc-subtle">unnamed</span>}
                    </td>
                    <td className="arc-mono">
                      {d.mac_address ?? <span className="arc-subtle">not set</span>}
                    </td>
                    <td className="arc-muted">
                      {[VENDORS.find((v) => v.key === d.vendor)?.label, d.model]
                        .filter(Boolean).join(' ') || <span className="arc-subtle">—</span>}
                      {d.provisioning_role && d.provisioning_role !== 'back_office' && (
                        <div className="arc-subtle" style={{ fontSize: 11 }}>
                          {roleNames.get(d.provisioning_role) ?? d.provisioning_role}
                        </div>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {d.lines.length === 0
                          ? <span className="arc-subtle">none</span>
                          : d.lines.map((l) => (
                              <Badge key={l.id} tone="muted">
                                <span className="arc-mono" style={{ fontSize: 11 }}>
                                  {l.extension_number}
                                </span>
                                {l.extension_name ? ` ${l.extension_name}` : ''}
                              </Badge>
                            ))}
                      </div>
                    </td>
                    <td>
                      <div className="arc-stack">
                        <Badge tone={STATUS_TONE[d.status] ?? 'muted'}>
                          <span className="arc-dot" aria-hidden="true" />{d.status}
                        </Badge>
                        {/*
                          Silenced is not the same as offline, so it is said
                          separately. The handset stays registered and reachable
                          for outgoing calls; it is simply left out of the
                          extension's ring — which is what "ring my mobile, not
                          my desk" actually means.
                        */}
                        {d.ring_enabled === false && (
                          <span className="arc-subtle" style={{ fontSize: 11 }}>
                            does not ring
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <Switch id={`desk-${d.id}`} checked={d.hot_desk_enabled === true}
                        disabled={deskToggle.isPending}
                        label={`${d.label ?? d.mac_address ?? 'Handset'} accepts hot desk logins`}
                        onChange={(on) => {
                          if (on || confirm(`Stop ${d.label ?? d.mac_address ?? 'this handset'} being a shared desk? Anyone logged in at it will be logged out.`)) {
                            deskToggle.mutate({ id: d.id, on });
                          }
                        }} />
                    </td>
                    <td className="arc-muted">
                      {d.last_seen_at
                        ? relativeTime(d.last_seen_at)
                        : <span className="arc-subtle">never</span>}
                    </td>
                    <td className="arc-td-right">
                      <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
                        {/* A provisioning URL is refused unless a window is
                            open or the phone is calling from the address it
                            already used. Replacing a handset therefore needs
                            this; without it the only route was an API call. */}
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                          disabled={save.isPending}
                          title={d.ring_enabled === false
                            ? 'Include this handset in the extension ring again'
                            : 'Stop this handset ringing, without disconnecting it'}
                          onClick={() => save.mutate({
                            id: d.id,
                            ring_enabled: d.ring_enabled === false,
                          })}>
                          {d.ring_enabled === false
                            ? 'Ring again' : 'Silence'}
                        </button>
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                          disabled={openProv.isPending}
                          title="Let this handset fetch its configuration again"
                          onClick={() => {
                            if (confirm(
                              `Allow ${d.label ?? d.mac_address ?? 'this handset'} to provision for the next 30 minutes?

`
                              + 'Use this when replacing the phone or moving it to a different network. '
                              + 'The provisioning URL does not change.')) {
                              openProv.mutate(d.id);
                            }
                          }}>Allow provisioning</button>
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                          onClick={() => { setEditing(d); setCreating(false); }}>Edit</button>
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                          onClick={() => {
                            if (confirm(`Remove ${d.label ?? d.mac_address ?? 'this handset'}? The phone will stop receiving configuration.`)) {
                              del.mutate(d.id);
                            }
                          }}>Remove</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {!devices.isLoading && rows.length === 0 && (
          <Empty icon="phone" title="No handsets"
            hint="Add a phone by its MAC address and assign extensions to its line keys." />
        )}
      </div>
    </>
  );
}

/**
 * Exported because the extension's own Phone setup section offers the same form.
 *
 * One handset form, not two. The MAC identifies the phone, the vendor decides
 * which generator answers it, and the line keys decide which seats appear on it
 * -- get any of them wrong in one of two copies and the phone fails to
 * provision with nothing on screen to say which copy was used.
 *
 * `seedExtensionId` is the only difference between the two callers: opened from
 * a seat, line 1 is that seat, because adding a handset there and leaving its
 * only line unassigned is a phone that provisions and then rings for nobody.
 */
export function DeviceEditor({ tenantId, existing, onDone, seedExtensionId }: {
  tenantId: string; existing: Device | null; onDone: () => void;
  seedExtensionId?: string;
}) {
  const save = useSaveDevice(tenantId);
  const [provisioned, setProvisioned] = useState<string | null>(null);
  const [issuedCred, setIssuedCred] = useState<
    { username: string; realm: string; password: string } | null>(null);
  const extensions = useExtensions(tenantId, { limit: 500 });

  const catalogue = useHandsetCatalogue();
  const roles = useProvisioningRoles();
  const [mac, setMac] = useState(existing?.mac_address ?? '');
  /**
   * The handset, from the catalogue. OTHER is a phone that is not in it; a
   * handset added before the catalogue keeps what it was typed in as until
   * somebody chooses its model.
   */
  const [modelId, setModelId] = useState<string>(existing ? (existing.handset_model_id ?? OTHER) : '');
  const [role, setRole] = useState<string>(existing?.provisioning_role ?? 'back_office');
  const [net, setNet] = useState<NetworkDraft>(() => draftOf(existing?.network));
  const [label, setLabel] = useState(existing?.label ?? '');
  const [lines, setLines] = useState<LineDraft[]>(() =>
    existing?.lines.map((l) => ({
      id: l.id, extensionId: l.extension_id, label: l.label ?? '',
    })) ?? [{ id: crypto.randomUUID(), extensionId: seedExtensionId ?? '', label: '' }],
  );

  const err = save.error instanceof ApiClientError ? save.error : null;
  const macNormalised = mac.replace(/[^0-9a-fA-F]/g, '');
  const macValid = mac === '' || macNormalised.length === 12;
  const models = catalogue.data?.data ?? [];
  const roleList = roles.data?.data ?? [];
  const chosen = models.find((m) => m.id === modelId) ?? null;
  const full = chosen !== null && lines.length >= chosen.max_lines;
  const staticIncomplete = net.mode === 'static' && (!net.ip.trim() || !net.netmask.trim() || !net.gateway.trim());

  /** What to send about the handset itself: the catalogue entry, or not listed. */
  const handsetFields = () => {
    if (modelId !== OTHER) return { handset_model_id: modelId };
    // Left as it was typed in, rather than overwritten, until a model is chosen.
    if (existing && !existing.handset_model_id) return {};
    return { handset_model_id: null, vendor: 'generic', model: null };
  };

  /**
   * Shown once, in place of the form.
   *
   * `{mac}` is left in the URL on purpose rather than substituted: most phones
   * append their own MAC to a provisioning base URL, and the ones that do not
   * need it spelled out. Showing the literal placeholder is what makes both
   * cases obvious to whoever is standing at the handset.
   */
  if (provisioned) {
    return (
      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <h3 className="arc-h2" style={{ marginBottom: 4 }}>Handset added</h3>
        <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 0 }}>
          Point the phone at this URL — DHCP option 66, or the provisioning
          field in its web interface. It is shown once and cannot be read back;
          delete and re-add the handset to get a new one.
        </p>
        <div className="arc-row" style={{ gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          <code className="arc-mono" style={{
            userSelect: 'all', fontSize: 12, padding: '7px 10px',
            background: 'var(--arc-surface-3)', borderRadius: 'var(--arc-r-sm)',
            wordBreak: 'break-all',
          }}>{provisioned}</code>
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
            onClick={() => void navigator.clipboard?.writeText(provisioned)}>
            Copy
          </button>
        </div>
        <p className="arc-muted" style={{ fontSize: 12, marginTop: 10 }}>
          The file carries the SIP password, so treat the link as a credential.
          Only Yealink handsets receive a full config today; other vendors get
          the settings listed for entering by hand.
        </p>

        {/*
          This handset's OWN credential, shown once.
          The provisioning file above carries it automatically, so most people
          never need it -- but a vendor we do not generate a config for has to
          be set up by hand, and this is the only moment the password is
          visible. It is the DEVICE's identity, not the extension's: revoking it
          stops this phone and leaves every other one on the seat working.
        */}
        {issuedCred && (
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--arc-border)' }}>
            <div style={{ fontWeight: 550, marginBottom: 6 }}>
              Or configure it by hand
            </div>
            <dl className="arc-kv">
              <dt>SIP username</dt><dd className="arc-mono">{issuedCred.username}</dd>
              <dt>Domain</dt><dd className="arc-mono">{issuedCred.realm}</dd>
              <dt>Password</dt>
              <dd className="arc-row" style={{ gap: 8 }}>
                <span className="arc-mono" style={{ fontWeight: 600, userSelect: 'all' }}>
                  {issuedCred.password}
                </span>
                <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                  onClick={() => void navigator.clipboard?.writeText(issuedCred.password)}>
                  Copy
                </button>
              </dd>
            </dl>
          </div>
        )}
        <div className="arc-row" style={{ gap: 8, marginTop: 12 }}>
          <button type="button" className="arc-btn arc-btn-primary"
            onClick={() => { setProvisioned(null); setIssuedCred(null); onDone(); }}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      className="arc-card arc-card-body" style={{ marginBottom: 14 }}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        save.mutate({
          ...(existing ? { id: existing.id } : {}),
          mac_address: mac || null,
          ...handsetFields(),
          label: label || null,
          provisioning_role: role,
          network: networkPayload(net),
          lines: lines
            .filter((l) => l.extensionId)
            .map((l, i) => ({
              extension_id: l.extensionId,
              line_number: i + 1,
              ...(l.label ? { label: l.label } : {}),
            })),
        }, {
          onSuccess: (saved) => {
            /**
             * Stay put when a provisioning URL came back.
             *
             * Closing on save is right when editing an existing handset; on
             * creation it would discard the one thing the phone needs. The URL
             * cannot be produced again — only the token's hash is stored.
             */
            if (saved?.sip_credential) setIssuedCred(saved.sip_credential);
            if (saved?.provisioning_url) setProvisioned(saved.provisioning_url);
            else onDone();
          },
        });
      }}
    >
      <h3 className="arc-h2" style={{ marginBottom: 12 }}>
        {existing ? `Edit ${existing.label ?? existing.mac_address ?? 'handset'}` : 'Add handset'}
      </h3>
      {err && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 12 }} role="alert">
          <Icon name="warning" size={14} /><span>{err.message}</span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="dv-label">Name</label>
          <input id="dv-label" className="arc-input" value={label}
            onChange={(e) => setLabel(e.target.value)} placeholder="Reception desk" />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="dv-mac">MAC address</label>
          <input id="dv-mac" className="arc-input arc-mono" value={mac}
            aria-invalid={!macValid}
            onChange={(e) => setMac(e.target.value)} placeholder="805e:c04d:1f2a" />
          {/* The MAC is what identifies the phone when it asks for its config,
              so a typo here is a phone that silently never provisions. */}
          <p className={macValid ? 'arc-hint' : 'arc-error'}>
            {macValid ? 'Printed on the underside of the phone.' : 'Needs 12 hex digits.'}
          </p>
        </div>
        <div className="arc-field" style={{ margin: 0, gridColumn: 'span 2' }}>
          <label className="arc-label" htmlFor="dv-model">Handset</label>
          <select id="dv-model" className="arc-input" value={modelId} required
            onChange={(e) => {
              const next = models.find((x) => x.id === e.target.value) ?? null;
              setModelId(e.target.value);
              // A role the new phone cannot do goes back to the default, rather
              // than failing on save for a reason the form did not show.
              if (!roleFits(roleList, role, next)) setRole('back_office');
            }}>
            {!existing && <option value="" disabled>Choose a handset…</option>}
            {byMake(models, modelId).map(([make, list]) => (
              <optgroup key={make} label={make}>
                {list.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.vendor_label} {m.model}{m.active ? '' : ' (no longer offered)'}
                  </option>
                ))}
              </optgroup>
            ))}
            <option value={OTHER}>
              {existing && !existing.handset_model_id && (existing.model || existing.vendor !== 'generic')
                ? `${VENDORS.find((v) => v.key === existing.vendor)?.label ?? existing.vendor} ${existing.model ?? ''} (not in the catalogue)`
                : 'Not in the catalogue'}
            </option>
          </select>
          <p className="arc-hint">
            {chosen
              ? `Holds ${chosen.max_lines} line${chosen.max_lines === 1 ? '' : 's'}. ${chosen.generates_config ? 'Its configuration is generated.' : 'Its settings are listed to enter by hand.'}`
              : modelId === OTHER ? 'Its settings are listed to enter by hand.' : 'From the supported-handset catalogue.'}
          </p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)', gap: 12, marginBottom: 16 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="dv-role">Role</label>
          <select id="dv-role" className="arc-input" value={role}
            onChange={(e) => setRole(e.target.value)}>
            {roleList.map((r) => (
              <option key={r.role} value={r.role} disabled={!roleFits(roleList, r.role, chosen)}>
                {r.name}{roleFits(roleList, r.role, chosen) ? '' : ' (needs a phone with a login screen)'}
              </option>
            ))}
          </select>
          <p className="arc-hint">
            {roleList.find((r) => r.role === role)?.description ?? 'What its keys are set up for.'}
          </p>
        </div>
        <NetworkFields value={net} onChange={setNet} />
      </div>

      <div className="arc-field">
        <div className="arc-row" style={{ marginBottom: 7 }}>
          <label className="arc-label" style={{ margin: 0 }}>Line keys</label>
          <div className="arc-spacer" />
          {full && (
            <span className="arc-subtle" style={{ fontSize: 11.5, marginRight: 8 }}>
              A {chosen?.model} holds {chosen?.max_lines}
            </span>
          )}
          <button type="button" className="arc-btn arc-btn-sm" disabled={full}
            onClick={() => setLines((p) => [
              ...p, { id: crypto.randomUUID(), extensionId: '', label: '' },
            ])}>
            <Icon name="plus" size={12} /> Add line
          </button>
        </div>
        {/* Order IS the line number: line 1 is the top key on the handset, so
            the list reads the way the phone looks. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {lines.map((l, i) => (
            <div key={l.id} className="arc-row" style={{ gap: 8 }}>
              <span className="arc-key-chip" style={{ width: 24, height: 24, fontSize: 11.5 }}>
                {i + 1}
              </span>
              <select className="arc-input" style={{ maxWidth: 300 }} value={l.extensionId}
                aria-label={`Line ${i + 1} extension`}
                onChange={(e) => setLines((p) =>
                  p.map((y) => y.id === l.id ? { ...y, extensionId: e.target.value } : y))}>
                <option value="">— not assigned —</option>
                {extensions.data?.data.map((e) => (
                  <option key={e.id} value={e.id}>{e.number} · {e.display_name}</option>
                ))}
              </select>
              <input className="arc-input" style={{ maxWidth: 200 }} placeholder="Key label (optional)"
                value={l.label} aria-label={`Line ${i + 1} label`}
                onChange={(e) => setLines((p) =>
                  p.map((y) => y.id === l.id ? { ...y, label: e.target.value } : y))} />
              <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon"
                aria-label={`Remove line ${i + 1}`}
                onClick={() => setLines((p) => p.filter((y) => y.id !== l.id))}>
                <Icon name="trash" size={13} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <button type="submit" className="arc-btn arc-btn-primary"
        disabled={save.isPending || !macValid || modelId === '' || staticIncomplete}>
        {save.isPending ? 'Saving…' : existing ? 'Save changes' : 'Add handset'}
      </button>
    </form>
  );
}

/** The handset select's value for a phone that is not in the catalogue. */
const OTHER = '__not_listed__';

/** Whether a phone can do a role. Only hot desking asks anything of the phone. */
function roleFits(roles: ProvisioningRoleInfo[], role: string, model: HandsetModel | null): boolean {
  const r = roles.find((x) => x.role === role);
  if (!r?.needs_xml_browser) return true;
  return model !== null && model.xml_browser && model.desk_pages;
}

/** Offered models grouped by make, keeping a retired one the handset is already on. */
function byMake(models: HandsetModel[], currentId: string): Array<[string, HandsetModel[]]> {
  const groups = new Map<string, HandsetModel[]>();
  for (const m of models) {
    if (!m.active && m.id !== currentId) continue;
    groups.set(m.vendor_label, [...(groups.get(m.vendor_label) ?? []), m]);
  }
  return [...groups];
}

interface NetworkDraft {
  mode: 'unmanaged' | 'dhcp' | 'static';
  ip: string; netmask: string; gateway: string; dns1: string; dns2: string; vlan: string;
}

const draftOf = (n: Device['network']): NetworkDraft => ({
  mode: n?.mode ?? 'unmanaged',
  ip: n?.ip ?? '', netmask: n?.netmask ?? '', gateway: n?.gateway ?? '',
  dns1: n?.dns[0] ?? '', dns2: n?.dns[1] ?? '',
  vlan: n?.vlan_id ? String(n.vlan_id) : '',
});

const networkPayload = (d: NetworkDraft) => {
  const fixed = d.mode === 'static';
  return {
    mode: d.mode,
    ip: fixed ? d.ip.trim() || null : null,
    netmask: fixed ? d.netmask.trim() || null : null,
    gateway: fixed ? d.gateway.trim() || null : null,
    dns: fixed ? [d.dns1, d.dns2].map((x) => x.trim()).filter(Boolean) : [],
    vlan_id: d.vlan.trim() ? Number(d.vlan) : null,
  };
};

/**
 * The phone's network, for a site without DHCP or with a voice VLAN.
 *
 * Left alone by default. A setting pushed to a phone somebody configured by
 * hand can take it off the network, and a phone off the network cannot fetch
 * the file that would put it right -- so the hint says so where the address is
 * typed, not in a document nobody opens first.
 */
function NetworkFields({ value, onChange }: { value: NetworkDraft; onChange: (d: NetworkDraft) => void }) {
  const field = (key: 'ip' | 'netmask' | 'gateway' | 'dns1' | 'dns2' | 'vlan', label: string, placeholder: string) => (
    <div className="arc-field" style={{ margin: 0 }}>
      <label className="arc-label" htmlFor={`dv-net-${key}`}>{label}</label>
      <input id={`dv-net-${key}`} className="arc-input arc-mono" value={value[key]} placeholder={placeholder}
        inputMode={key === 'vlan' ? 'numeric' : 'decimal'}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ ...value, [key]: e.target.value })} />
    </div>
  );
  return (
    <div className="arc-field" style={{ margin: 0 }}>
      <span className="arc-label">Network</span>
      <Segmented label="Network settings" value={value.mode}
        options={[['unmanaged', 'As the phone has it'], ['dhcp', 'DHCP'], ['static', 'Static address']]}
        onChange={(mode) => onChange({ ...value, mode })} />
      {value.mode === 'static' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginTop: 10 }}>
          {field('ip', 'Address', '10.0.5.20')}
          {field('netmask', 'Netmask', '255.255.255.0')}
          {field('gateway', 'Gateway', '10.0.5.1')}
          {field('dns1', 'DNS', '10.0.5.2')}
          {field('dns2', 'Second DNS', 'optional')}
        </div>
      )}
      <div style={{ maxWidth: 140, marginTop: 10 }}>
        {field('vlan', 'Voice VLAN', 'none')}
      </div>
      <p className="arc-hint">
        {value.mode === 'static'
          ? 'Written into the phone. A wrong address takes it off the network, where it cannot fetch the correction.'
          : value.mode === 'dhcp'
            ? 'Switches the phone to DHCP on its next fetch.'
            : 'Nothing about the network is written unless it is chosen here.'}
      </p>
    </div>
  );
}
